import clsx from 'clsx';
import { createElement, forwardRef, HTMLAttributes, PropsWithChildren } from 'react';
import { useThemeColorVar } from '../../hooks/use-theme-color-var';
import { ThemeColorsValues, ThemeTypographyVariantElementMap, ThemeTypographyVariants } from '../../theme';
import classes from './typography.module.scss';

export type TypographyProps = PropsWithChildren
  & Omit<HTMLAttributes<HTMLElement>, 'ref'> & {
  /**
   * Custom HTML element to render
   */
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'label';

    /**
   * Custom appearance
   */
    variant?: ThemeTypographyVariants;

    /**
   * Custom classname
   */
    className?: string;

    /**
   * Custom color definition
   */
    color?: ThemeColorsValues | 'inherit';

    /**
   * Render as bold text
   */
    bold?: boolean;

    /**
   * Add text overflow
   */
    noWrap?: boolean;

    /**
   * Preserves white-space but allows line breaks where necessary
   */
    preWrap?: boolean;

    /**
   * Align text in center
   */
    center?: boolean;

    /**
   * Specifies how to capitalize text
   */
    textTransform?: 'capitalize' | 'uppercase' | undefined;

    /**
   * Are children HTML
   */
    isHtml?: boolean;

    /**
   * Indicates whether the text should be underlined or not.
   *
   * This property is optional and can be set to a boolean value.
   *
   * If set to `true`, the text will be displayed with an underline.
   *
   * If set to `false`, the text will be displayed without an underline.
   *
   * If not provided, the default value is `undefined`.
   */
    underline?: boolean;

    /**
   *
   */
    htmlFor?: string;
  };

/**
 * Typography component used for typographical output
 */
export const Typography = forwardRef<HTMLParagraphElement, TypographyProps>(
  (
    {
      as,
      bold,
      color,
      className,
      noWrap,
      preWrap,
      variant = 'paragraph',
      children,
      center,
      textTransform,
      isHtml = false,
      underline,
      ...props
    },
    ref
  ) => {
    const customColor = useThemeColorVar(color);

    const elementProps = {
      ...props,
      ref,
      style: {
        '--typography--color': color === 'inherit' ? 'inherit' : customColor,
      },
      className: clsx(
        'typography-component',
        classes.root,
        className,
        {
          [classes.bold]: bold,
          [classes.center]: center,
          [classes.noWrap]: noWrap,
          [classes.preWrap]: preWrap,
          [classes.underline]: underline,
          ...(textTransform ? { [classes[textTransform]]: textTransform } : {}),
        },
        classes[`type_${variant}`]
      ),
    };

    if (isHtml && typeof children === 'string') {
      elementProps['dangerouslySetInnerHTML'] = { __html: children };
    } else {
      // eslint-disable-next-line
      // @ts-ignore
      elementProps['children'] = children;
    }

    return createElement(
      as ?? ThemeTypographyVariantElementMap[variant] ?? 'span',
      elementProps
    );
  }
);
